import React, { useEffect, useContext, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Input } from "antd";
import { ExclamationCircleOutlined, WarningFilled } from "@ant-design/icons";
import { AppContext } from "../../contexts/AppContext";
import SubHeader from "../../components/SubHeader/SubHeader";
import GeneralButton from "../../components/GeneralButton/GeneralButton";
import { validateEmail } from "../../utils/validationUtils";
import { patientWebLogin, getUserInfo } from "../../services/service";
import { showToast, encodeToBase64 } from "../../utils/commonUtils";
import "../../styles/globals.css";
import "./AccountLoginPage.css";

/**
 * アカウントログイン画面用のコンポーネント
 * @param {} なし
 * @return {} なし
 */
function AccountLoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    globalMedicalInstitutionName,
    setGlobalLoginUserInfo,
    setGlobalEmailAddress,
    setGlobalAccessToken,
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });

  const [errorMessage, setErrorMessage] = useState(null);
  const [inputMailValue, setInputMailValue] = useState(null);
  const [inputPasswordValue, setInputPasswordValue] = useState(null);

  const emailInput = useRef(null);

  useEffect(() => {
    emailInput.current.input.focus();
  }, [errors?.email]);

  const handleLoginClick = async () => {
    // ログイン処理
    await form.validateFields();
    const values = form.getFieldsValue();
    const { email, password } = values;

    try {
      const data = await patientWebLogin(email, encodeToBase64(password));
      // トークンを取得
      if (data.result.status === "OK") {
        // アクセストークンの保持
        setGlobalAccessToken(data.data.access_token);

        // トークンを使用してユーザー情報を取得
        const userInfo = await getUserInfo(data.data.access_token);

        if (userInfo.result.status === "OK") {
          // ユーザ情報保持
          setGlobalLoginUserInfo(userInfo?.data);

          // メールアドレス保持
          setGlobalEmailAddress(email);
          if (location.pathname.includes("mypage")) {
            // マイページに遷移
            navigate("/mypage/menu");
          } else {
            // 受診者選択画面に遷移
            navigate("/patient-select");
          }
        } else {
          // エラーメッセージセット
          setErrorMessage(data.result.error_message);
        }
      } else {
        // エラーメッセージセット
        setErrorMessage(data.result.error_message);
      }
    } catch (error) {
      console.error("Error patientWebLogin:", error);
      showToast("ログインに失敗しました", "error");
    }
  };

  /**
   * 値変更時の処理
   * @param {string} fieldName - フィールド名
   */
  const onValueChange = async (fieldName) => {
    try {
      await form.validateFields([fieldName]);
      setErrors({ ...errors, [fieldName]: false });
      setErrorMessage(null);
    } catch (error) {
      setErrors({ ...errors, [fieldName]: true });
    }
  };

  /**
   * メールアドレス入力値変更時の処理
   * @param {object} e - イベントオブジェクト
   */
  const handleInputMailChange = (e) => {
    setInputMailValue(e.target.value);
    onValueChange("email");
  };
  /**
   * パスワード入力値変更時の処理
   * @param {object} e - イベントオブジェクト
   */
  const handleInputPasswordChange = (e) => {
    setInputPasswordValue(e.target.value);
    onValueChange("password");
  };
  return (
    <div className="account-login">
      <SubHeader clinicName={globalMedicalInstitutionName} />
      <div className="main-area">
        <div className="title">メールアドレスでログイン</div>
        <div className="form">
          <Form
            form={form}
            name="login"
            layout="vertical"
            initialValues={{ remember: true }}
          >
            <Form.Item
              className="ant-label"
              label="メールアドレス"
              name="email"
              rules={[
                { required: true, message: "メールアドレスを入力してください" },
                { validator: validateEmail },
              ]}
              validateStatus={errors.email ? "error" : ""}
              help={
                errors.email ? (
                  <span className="ant-validation-message">
                    メールアドレスを入力してください
                  </span>
                ) : (
                  ""
                )
              }
            >
              <Input
                className="ant-input"
                placeholder="例)example@example.com"
                ref={emailInput}
                suffix={errors.email ? <ExclamationCircleOutlined /> : null}
                onChange={handleInputMailChange}
              />
            </Form.Item>
            <Form.Item
              className="ant-label"
              label="パスワード"
              name="password"
              rules={[
                { required: true, message: "パスワードを入力してください" },
              ]}
              validateStatus={errors.password ? "error" : ""}
              help={
                errors.password ? (
                  <span className="ant-validation-message">
                    パスワードを入力してください
                  </span>
                ) : (
                  ""
                )
              }
            >
              <Input.Password
                className="ant-input"
                placeholder="パスワード"
                suffix={errors.password ? <ExclamationCircleOutlined /> : null}
                onChange={handleInputPasswordChange}
              />
            </Form.Item>
            {errorMessage && (
              <div className="error-area">
                <div className="icon-area">
                  <WarningFilled className="icon" />
                </div>
                <label>{errorMessage}</label>
              </div>
            )}
            <Form.Item>
              <GeneralButton
                buttonLabel="ログイン"
                type={
                  Object.values(errors).some((error) => error) ||
                  inputMailValue == null ||
                  inputPasswordValue == null
                    ? "disabled"
                    : "primary-blue"
                }
                onClick={handleLoginClick}
                disabled={Object.values(errors).some((error) => error)}
              />
            </Form.Item>
          </Form>

          <div className="explanation-area">
            パスワードをお忘れの方は
            <a href="/reset-password" rel="noopener noreferrer">
              こちら
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountLoginPage;
